import {
  Component,
  input,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  ElementRef,
  AfterViewInit,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Marked } from "marked";
import hljs from "highlight.js";
import * as katex from "katex";
import { completePartialMarkdown } from "@copilotkit/core";
import { LucideAngularModule } from "lucide-angular";
import { injectChatLabels } from "../core/chat-labels";

@Component({
  standalone: true,
  selector: "agent-chat-assistant-message-renderer",
  imports: [CommonModule, LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: ` <div #markdownContainer [class]="inputClass()" (click)="handleClick($event)"></div> `,
})
export class AgentChatAssistantMessageRenderer implements AfterViewInit {
  readonly content = input<string>("");

  readonly inputClass = input<string | undefined>();

  readonly labels = injectChatLabels();

  @ViewChild("markdownContainer", { static: false })
  markdownContainer?: ElementRef<HTMLDivElement>;

  private elementRef = inject(ElementRef);

  private copyStates = new Map<string, boolean>();

  readonly renderedHtml = computed(() => {
    const currentContent = this.content();
    const completedMarkdown = completePartialMarkdown(currentContent);
    return this.renderMarkdown(completedMarkdown);
  });

  constructor() {
    effect(() => {
      this.content();
      this.copyStates.clear();
      if (this.markdownContainer) {
        this.updateContent();
        this.renderMathEquations();
      }
    });
  }

  ngAfterViewInit(): void {
    this.updateContent();
    this.renderMathEquations();
  }

  private updateContent(): void {
    if (!this.markdownContainer) return;
    const container = this.markdownContainer.nativeElement;
    const html = this.renderedHtml();
    container.innerHTML = html;
  }

  private codeBlocksMap = new Map<string, string>();

  private markedInstance: Marked | null = null;

  private initializeMarked(): void {
    if (this.markedInstance) return;
    const highlightedBlocks = new Map<string, string>();
    this.markedInstance = new Marked();
    this.markedInstance.setOptions({ gfm: true, breaks: true });
    this.markedInstance.use({
      walkTokens: (token: any) => {
        if (token.type === "code") {
          const rawCode = token.text;
          const lang = token.lang || "";
          const blockId = this.generateBlockId(rawCode);
          this.codeBlocksMap.set(blockId, rawCode);
          const copyLabel = this.labels.assistantMessageToolbarCopyCodeLabel;
          const language = hljs.getLanguage(lang) ? lang : "plaintext";
          const highlighted = hljs.highlight(rawCode, { language }).value;
          const codeClass = lang ? `hljs language-${lang}` : "hljs";
          const fullHtml = `
            <div class="code-block-container">
              <div class="code-block-header">
                ${lang ? `<span class="code-block-language">${lang}</span>` : "<span></span>"}
                <button
                  class="code-block-copy-button"
                  data-code-block-id="${blockId}"
                  aria-label="${copyLabel} code">
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.11 0-2-.9-2-2V4c0-1.11.89-2 2-2h10c1.11 0 2 .89 2 2"/></svg>
                  <span>${copyLabel}</span>
                </button>
              </div>
              <pre><code class="${codeClass}">${highlighted}</code></pre>
            </div>
          `;
          highlightedBlocks.set(blockId, fullHtml);
          token.type = "html";
          token.text = fullHtml;
        }
      },
    });
  }

  private renderMarkdown(content: string): string {
    this.initializeMarked();
    this.codeBlocksMap.clear();
    let html = this.markedInstance!.parse(content) as string;
    html = this.processMathEquations(html);
    return html;
  }

  private processMathEquations(html: string): string {
    const codeBlocks: string[] = [];
    const placeholder = "___CODE_BLOCK_PLACEHOLDER_";
    html = html.replace(/<pre><code[\s\S]*?<\/code><\/pre>/g, (match) => {
      const index = codeBlocks.length;
      codeBlocks.push(match);
      return `${placeholder}${index}___`;
    });
    const inlineCode: string[] = [];
    const inlinePlaceholder = "___INLINE_CODE_PLACEHOLDER_";
    html = html.replace(/<code>[\s\S]*?<\/code>/g, (match) => {
      const index = inlineCode.length;
      inlineCode.push(match);
      return `${inlinePlaceholder}${index}___`;
    });
    html = html.replace(/\$\$([\s\S]*?)\$\$/g, (match, equation) => {
      try {
        return katex.renderToString(equation, { displayMode: true, throwOnError: false });
      } catch {
        return match;
      }
    });
    html = html.replace(/\$([^$]+)\$/g, (match, equation) => {
      try {
        return katex.renderToString(equation, { displayMode: false, throwOnError: false });
      } catch {
        return match;
      }
    });
    codeBlocks.forEach((block, index) => {
      html = html.replace(`${placeholder}${index}___`, block);
    });
    inlineCode.forEach((code, index) => {
      html = html.replace(`${inlinePlaceholder}${index}___`, code);
    });
    return html;
  }

  private renderMathEquations(): void {
    if (!this.markdownContainer) return;
    const container = this.markdownContainer.nativeElement;
    const mathElements = container.querySelectorAll(".math-placeholder");
    mathElements.forEach((element) => {
      const equation = element.getAttribute("data-equation");
      const displayMode = element.getAttribute("data-display") === "true";
      if (equation) {
        try {
          katex.render(equation, element as HTMLElement, {
            displayMode,
            throwOnError: false,
          });
        } catch (error) {
          console.error("Failed to render math equation:", error);
        }
      }
    });
  }

  handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const copyButton = target.closest(".code-block-copy-button") as HTMLButtonElement;
    if (copyButton) {
      event.preventDefault();
      const blockId = copyButton.getAttribute("data-code-block-id");
      if (blockId) {
        const code = this.codeBlocksMap.get(blockId);
        if (code) {
          this.copyCodeBlock(blockId, code);
        }
      }
    }
  }

  private copyCodeBlock(blockId: string, code: string): void {
    navigator.clipboard.writeText(code).then(
      () => {
        const button = this.elementRef.nativeElement.querySelector(`[data-code-block-id="${blockId}"]`);
        if (button) {
          const originalHTML = button.innerHTML;
          button.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            <span>${this.labels.assistantMessageToolbarCopyCodeCopiedLabel}</span>
          `;
          button.setAttribute("aria-label", `${this.labels.assistantMessageToolbarCopyCodeCopiedLabel} code`);
          setTimeout(() => {
            button.innerHTML = originalHTML;
            button.setAttribute("aria-label", `${this.labels.assistantMessageToolbarCopyCodeLabel} code`);
          }, 2000);
        }
      },
      (err) => {
        console.error("Failed to copy code:", err);
      },
    );
  }

  private generateBlockId(code: string): string {
    let hash = 0;
    for (let i = 0; i < code.length; i++) {
      const char = code.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return `code-block-${hash}`;
  }
}
